// Story 1.27 — admin queue list. Wrapped by <AdminGuard> in App.tsx.
//
// Since renamed to the "Creators" console: two tabs, both filterable —
//   * Applications: the original review queue (filter state in the URL
//     query string so deep-links + browser-back work; row click
//     navigates to the detail page).
//   * All creators: every approved creator, with the same quick-view
//     dialog the composer uses (preview share + full profile).
// Tab choice lives in ?tab= so it deep-links too.

import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Link, useSearchParams } from "react-router-dom";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@greenhold/components";

import { EmptyState } from "../components/EmptyState";
import {
  useBulkReviewCreatorApplications,
  type BulkReviewOutcome,
} from "../features/admin/useBulkReviewCreatorApplications";

import {
  DEFAULT_FILTERS,
  type AdminApplicationsFilters,
  type AdminCreatorApplicationStatus,
  useAdminCreatorApplications,
} from "../features/admin/useAdminCreatorApplications";
import { AllCreatorsPanel } from "../features/admin/AllCreatorsPanel";

const BULK_REJECT_MIN_CHARS = 20;
const BULK_REJECT_MAX_CHARS = 1000;

function statusBadgeVariant(
  status: "pending" | "approved" | "rejected" | "withdrawn"
): "warning" | "success" | "destructive" | "secondary" {
  switch (status) {
    case "pending":
      return "warning";
    case "approved":
      return "success";
    case "rejected":
      return "destructive";
    case "withdrawn":
      return "secondary";
  }
}

function formatRelative(iso: string): string {
  // Minimal relative formatter to avoid a new dependency. Returns
  // "Nm ago", "Nh ago", or "Nd ago" — sufficient for the queue UX.
  const then = new Date(iso).getTime();
  const ms = Date.now() - then;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function filtersFromSearchParams(params: URLSearchParams): AdminApplicationsFilters {
  const status = (params.get("status") as AdminCreatorApplicationStatus | null) ?? "pending";
  return {
    status,
    submitted_since: params.get("submitted_since") ?? undefined,
    submitted_until: params.get("submitted_until") ?? undefined,
    display_name_search: params.get("display_name_search") ?? undefined,
    page: Number(params.get("page") ?? "1") || 1,
    page_size: Number(params.get("page_size") ?? String(DEFAULT_FILTERS.page_size)) || DEFAULT_FILTERS.page_size,
  };
}

export function AdminApplicationsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => filtersFromSearchParams(searchParams),
    [searchParams]
  );

  const query = useAdminCreatorApplications(filters);

  const bulkReview = useBulkReviewCreatorApplications();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showBulkReject, setShowBulkReject] = useState(false);
  const [bulkRejectReason, setBulkRejectReason] = useState("");

  // Only PENDING rows are actionable (approve/reject). Selection is scoped
  // to the current page: clear it whenever the query (filters / page / tab)
  // changes so ids from a prior page can never be acted on.
  const rows = query.data?.data ?? [];
  const selectablePendingIds = useMemo(
    () =>
      rows.filter((r) => r.status === "pending").map((r) => r.application_id),
    [rows],
  );
  useEffect(() => {
    setSelected(new Set());
  }, [searchParams]);

  const selectedIds = useMemo(
    () => selectablePendingIds.filter((id) => selected.has(id)),
    [selectablePendingIds, selected],
  );
  const allPendingSelected =
    selectablePendingIds.length > 0 &&
    selectablePendingIds.every((id) => selected.has(id));

  function toggleRow(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllPending() {
    setSelected(allPendingSelected ? new Set() : new Set(selectablePendingIds));
  }

  function reportOutcome(verb: string, outcome: BulkReviewOutcome) {
    const ok = outcome.succeeded.length;
    const failed = outcome.failed.length;
    if (failed === 0) {
      toast.success(`${verb} ${ok} application${ok === 1 ? "" : "s"}.`);
    } else if (ok === 0) {
      toast.error(`All ${failed} failed — e.g. ${outcome.failed[0].message}`);
    } else {
      toast(`${verb} ${ok}, ${failed} failed.`);
    }
    setSelected(new Set());
  }

  async function handleBulkApprove() {
    if (selectedIds.length === 0) return;
    const outcome = await bulkReview.mutateAsync({
      decision: "approve",
      ids: selectedIds,
    });
    reportOutcome("Approved", outcome);
  }

  async function confirmBulkReject() {
    if (selectedIds.length === 0) return;
    const outcome = await bulkReview.mutateAsync({
      decision: "reject",
      ids: selectedIds,
      reason: bulkRejectReason.trim(),
    });
    setShowBulkReject(false);
    setBulkRejectReason("");
    reportOutcome("Rejected", outcome);
  }

  function updateFilter<K extends keyof AdminApplicationsFilters>(
    key: K,
    value: AdminApplicationsFilters[K] | undefined
  ) {
    const next = new URLSearchParams(searchParams);
    if (value === undefined || value === "" || value === null) {
      next.delete(key);
    } else {
      next.set(key, String(value));
    }
    // Reset page on filter change.
    if (key !== "page") next.delete("page");
    setSearchParams(next);
  }

  function goToPage(page: number) {
    const next = new URLSearchParams(searchParams);
    next.set("page", String(Math.max(1, page)));
    setSearchParams(next);
  }

  const tab = searchParams.get("tab") === "creators" ? "creators" : "applications";

  function setTab(next: "applications" | "creators") {
    const params = new URLSearchParams(searchParams);
    if (next === "applications") {
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    setSearchParams(params);
  }

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900 p-6">
      <div className="max-w-6xl mx-auto space-y-[var(--gh-rhythm-admin,16px)]">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold">Creators</h1>
            <p className="opacity-70 text-sm">
              {tab === "applications"
                ? "Review and decide pending applications."
                : "Every approved creator on the platform."}
            </p>
          </div>
          {tab === "applications" &&
          query.data?.total_pending !== null &&
          query.data?.total_pending !== undefined ? (
            <Badge variant="warning">
              {query.data.total_pending} pending
            </Badge>
          ) : null}
        </header>

        {/* Tab switcher */}
        <div
          role="tablist"
          aria-label="Creators sections"
          className="flex items-center gap-2 border-b border-brand-forest-200"
        >
          {(
            [
              { key: "applications", label: "Applications" },
              { key: "creators", label: "All creators" },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={[
                "px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors",
                tab === t.key
                  ? "border-brand-forest-700 text-brand-forest-900"
                  : "border-transparent opacity-60 hover:opacity-100",
              ].join(" ")}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "creators" ? (
          <AllCreatorsPanel />
        ) : (
          <>
        {/* Filter bar */}
        <Card>
          <CardContent className="flex flex-wrap gap-3 items-end p-4">
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Status</span>
              <select
                className="border rounded px-2 py-1 bg-white"
                value={filters.status}
                onChange={(e) =>
                  updateFilter(
                    "status",
                    e.target.value as AdminCreatorApplicationStatus
                  )
                }
              >
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="withdrawn">Withdrawn</option>
                <option value="any">Any</option>
              </select>
            </label>
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Display-name search</span>
              <input
                type="text"
                className="border rounded px-2 py-1 bg-white"
                placeholder="e.g. tama"
                value={filters.display_name_search ?? ""}
                onChange={(e) =>
                  updateFilter("display_name_search", e.target.value)
                }
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Submitted since</span>
              <input
                type="date"
                className="border rounded px-2 py-1 bg-white"
                value={filters.submitted_since?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "submitted_since",
                    e.target.value ? `${e.target.value}T00:00:00Z` : undefined
                  )
                }
              />
            </label>
            <label className="text-sm">
              <span className="block mb-1 opacity-70">Submitted until</span>
              <input
                type="date"
                className="border rounded px-2 py-1 bg-white"
                value={filters.submitted_until?.slice(0, 10) ?? ""}
                onChange={(e) =>
                  updateFilter(
                    "submitted_until",
                    e.target.value ? `${e.target.value}T23:59:59Z` : undefined
                  )
                }
              />
            </label>
          </CardContent>
        </Card>

        {/* Bulk action bar — appears once one or more pending rows are
            selected; fans out over the single approve/reject endpoints. */}
        {selectedIds.length > 0 ? (
          <div className="flex items-center gap-3 rounded-md border border-brand-forest-300 bg-brand-paper-100 px-4 py-3">
            <span className="text-sm font-medium">
              {selectedIds.length} selected
            </span>
            <div className="flex-1" />
            <Button
              variant="primary"
              onClick={handleBulkApprove}
              disabled={bulkReview.isPending}
            >
              {bulkReview.isPending
                ? "Working…"
                : `Approve ${selectedIds.length}`}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setShowBulkReject(true)}
              disabled={bulkReview.isPending}
            >
              Reject {selectedIds.length}
            </Button>
            <Button
              variant="tertiary"
              onClick={() => setSelected(new Set())}
              disabled={bulkReview.isPending}
            >
              Clear
            </Button>
          </div>
        ) : null}

        {/* Results */}
        {query.isPending ? (
          <Card>
            <CardContent className="p-8 text-center opacity-70">
              Loading…
            </CardContent>
          </Card>
        ) : query.isError ? (
          <Card>
            <CardContent className="p-6 text-center space-y-3">
              <p className="text-destructive">
                {query.error.message ?? "Failed to load applications."}
              </p>
              <Button onClick={() => query.refetch()} variant="secondary">
                Retry
              </Button>
            </CardContent>
          </Card>
        ) : query.data.data.length === 0 ? (
          <EmptyState
            aria-label="No applications"
            description={
              filters.status === "pending"
                ? "Queue's clear — every pending application has been reviewed."
                : "No applications match these filters."
            }
          />
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Showing {query.data.data.length} application
                {query.data.data.length === 1 ? "" : "s"}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-brand-paper-100 text-left">
                  <tr>
                    <th className="px-2 py-2 w-8">
                      {selectablePendingIds.length > 0 ? (
                        <input
                          type="checkbox"
                          aria-label="Select all pending applications on this page"
                          checked={allPendingSelected}
                          onChange={toggleSelectAllPending}
                        />
                      ) : null}
                    </th>
                    <th className="px-4 py-2">Display name</th>
                    <th className="px-4 py-2">Email</th>
                    <th className="px-4 py-2">Region</th>
                    <th className="px-4 py-2">Submitted</th>
                    <th className="px-4 py-2">Status</th>
                    <th className="px-4 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.data.map((row) => (
                    <tr
                      key={row.application_id}
                      className="border-t border-brand-paper-200 hover:bg-brand-paper-100"
                    >
                      <td className="px-2 py-2">
                        {row.status === "pending" ? (
                          <input
                            type="checkbox"
                            aria-label={`Select ${row.display_name}`}
                            checked={selected.has(row.application_id)}
                            onChange={() => toggleRow(row.application_id)}
                          />
                        ) : null}
                      </td>
                      <td className="px-4 py-2 font-medium">
                        {row.display_name}
                      </td>
                      <td className="px-4 py-2 opacity-80 font-mono text-xs">
                        {row.applicant_email}
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {row.region} · {row.language}
                      </td>
                      <td className="px-4 py-2 opacity-80">
                        {formatRelative(row.submitted_at)}
                      </td>
                      <td className="px-4 py-2">
                        <Badge variant={statusBadgeVariant(row.status)}>
                          {row.status}
                        </Badge>
                      </td>
                      <td className="px-4 py-2">
                        <Link
                          to={`/admin/applications/${row.application_id}`}
                          className="text-brand-forest-700 underline decoration-dotted hover:decoration-solid"
                        >
                          Review →
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {/* Pagination */}
        {query.data && (query.data.has_next || filters.page > 1) ? (
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page - 1)}
              disabled={filters.page <= 1}
            >
              ← Prev
            </Button>
            <span className="text-sm opacity-70 font-mono">
              Page {filters.page}
            </span>
            <Button
              variant="secondary"
              onClick={() => goToPage(filters.page + 1)}
              disabled={!query.data.has_next}
            >
              Next →
            </Button>
          </div>
        ) : null}
          </>
        )}
      </div>

      {/* Bulk-reject reason modal — one shared reason applied to all
          selected applicants (mirrors the single-reject flow). */}
      {showBulkReject ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle>
                Reject {selectedIds.length} application
                {selectedIds.length === 1 ? "" : "s"}?
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm opacity-80">
                The same reason is emailed to every selected applicant. This
                can&apos;t be undone.
              </p>
              <label className="block text-sm">
                <span className="block mb-1 opacity-70">
                  Reason ({BULK_REJECT_MIN_CHARS}–{BULK_REJECT_MAX_CHARS} chars)
                </span>
                <textarea
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  rows={4}
                  value={bulkRejectReason}
                  onChange={(e) => setBulkRejectReason(e.target.value)}
                  maxLength={BULK_REJECT_MAX_CHARS}
                />
              </label>
              <div className="flex justify-end gap-2">
                <Button
                  variant="tertiary"
                  onClick={() => setShowBulkReject(false)}
                  disabled={bulkReview.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={confirmBulkReject}
                  disabled={
                    bulkReview.isPending ||
                    bulkRejectReason.trim().length < BULK_REJECT_MIN_CHARS
                  }
                >
                  {bulkReview.isPending
                    ? "Rejecting…"
                    : `Reject ${selectedIds.length}`}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
    </main>
  );
}
